<template>
  <div class="auth-wrapper">
    <div class="auth-container">
      <div class="auth-header">
        <h1 class="auth-title">平台管理登入</h1>
        <p class="auth-subtitle">請使用平台管理員帳號登入</p>
      </div>

      <div class="auth-form">
        <form @submit.prevent="handleLogin">
          <div class="form-group">
            <label for="platformCode">平台代碼</label>
            <input
              id="platformCode"
              v-model="form.platformCode"
              type="text"
              class="form-control"
              placeholder="請輸入平台代碼（不分大小寫）"
              required
            />
          </div>

          <div class="form-group">
            <label for="username">登入帳號</label>
            <input
              id="username"
              v-model="form.username"
              type="text"
              class="form-control"
              placeholder="請輸入登入帳號"
              required
              autocomplete="username"
            />
          </div>

          <div class="form-group">
            <label for="password">密碼</label>
            <input
              id="password"
              v-model="form.password"
              type="password"
              class="form-control"
              placeholder="請輸入密碼"
              required
              autocomplete="current-password"
            />
          </div>

          <div v-if="errorMessage" class="alert alert-danger">
            {{ errorMessage }}
          </div>

          <button
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="loading"
          >
            <span v-if="loading">登入中...</span>
            <span v-else>登入</span>
          </button>
        </form>
      </div>

      <div class="auth-footer">
        <p class="help-text">
          如有帳號問題，請聯絡系統管理員
        </p>
        <p class="switch-links">
          <router-link to="/client/login">客戶登入</router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue';
import { useRouter } from 'vue-router';
import authService from '@/helpers/authservice/authService.js';

export default {
  name: 'PlatformLogin',
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const errorMessage = ref('');

    const form = reactive({
      platformCode: '',
      username: '',
      password: '',
    });

    const handleLogin = async () => {
      loading.value = true;
      errorMessage.value = '';

      try {
        const result = await authService.platformLogin(
          form.platformCode,
          form.username,
          form.password
        );

        if (result.success) {
          router.push('/platform/dashboard');
        } else {
          errorMessage.value = result.error || '登入失敗，請檢查帳號密碼';
        }
      } catch (error) {
        // 優先從 error.data.message 提取（axios interceptor 設置的）
        // 其次從 error.response.data.message 提取
        // 最後使用 error.message
        let errorMsg = '登入失敗，請稍後再試';
        
        if (error.data && error.data.message) {
          errorMsg = error.data.message;
        } else if (error.response && error.response.data && error.response.data.message) {
          errorMsg = error.response.data.message;
        } else if (error.message && !error.message.includes('status code')) {
          // 如果 error.message 不是 axios 的默認錯誤訊息，才使用它
          errorMsg = error.message;
        }
        
        console.error('Login error:', {
          error,
          errorData: error.data,
          errorResponse: error.response,
          errorMessage: errorMsg
        });
        
        errorMessage.value = errorMsg;
      } finally {
        loading.value = false;
      }
    };

    return {
      form,
      loading,
      errorMessage,
      handleLogin,
    };
  },
};
</script>

<style scoped>
.auth-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  padding: 20px;
}

.auth-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 420px;
  padding: 40px;
}

.auth-header {
  text-align: center;
  margin-bottom: 30px;
}

.auth-title {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.auth-subtitle {
  color: #666;
  font-size: 14px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #f5576c;
  box-shadow: 0 0 0 3px rgba(245, 87, 108, 0.1);
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(245, 87, 108, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
}

.alert {
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-danger {
  background-color: #fee;
  color: #c33;
  border: 1px solid #fcc;
}

.auth-footer {
  margin-top: 24px;
  text-align: center;
  font-size: 14px;
}

.auth-footer .help-text {
  color: #666;
  margin: 0 0 12px 0;
}

.auth-footer .switch-links {
  margin: 0;
}

.auth-footer .switch-links a {
  color: #f5576c;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;
}

.auth-footer .switch-links a:hover {
  color: #d43f5a;
  text-decoration: underline;
}
</style>
